import { useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOHead from '@/components/feature/SEOHead';
import { articles } from '@/mocks/articles';
import { videoCollections, trendingVideos } from '@/mocks/videos';
import { siteConfig } from '@/mocks/site';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();

  const article = useMemo(() => {
    const found = articles.find((a) => a.id === id);
    if (found) return { type: 'article' as const, data: found };

    const collection = videoCollections.find((c) => c.id === id);
    if (collection) return { type: 'collection' as const, data: collection };

    const trending = trendingVideos.find((t) => t.id === id);
    if (trending) return { type: 'video' as const, data: trending };

    return null;
  }, [id]);

  if (!article) {
    return (
      <>
        <Navbar />
        <main className="pt-16 px-4 md:px-6 py-20 text-center min-h-screen flex items-center justify-center">
          <div>
            <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-5">
              <i className="ri-error-warning-line text-foreground-400 text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">内容未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">抱歉，您访问的内容不存在或已被移除。</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium whitespace-nowrap hover:bg-primary-600 transition-colors">
              <i className="ri-home-4-line"></i> 返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const isArticle = article.type === 'article';
  const data = article.data as typeof articles[0];

  const pageTitle = isArticle ? `${data.title} - ${siteConfig.name}` : `${data.title} - ${siteConfig.name}`;
  const pageDesc = isArticle ? data.excerpt : (data as typeof videoCollections[0]).description;
  const articleSchema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: siteConfig.url },
          { '@type': 'ListItem', position: 2, name: isArticle ? data.category : '视频合集', item: `${siteConfig.url}/category/${isArticle ? (data as typeof articles[0]).categorySlug : 'yingshidaquan'}` },
          { '@type': 'ListItem', position: 3, name: data.title },
        ],
      },
      isArticle
        ? {
            '@type': 'Article',
            headline: data.title,
            description: data.excerpt,
            author: { '@type': 'Person', name: data.author },
            datePublished: data.date,
            url: `${siteConfig.url}/article/${data.id}`,
            publisher: { '@type': 'Organization', name: siteConfig.name },
          }
        : {
            '@type': 'VideoObject',
            name: data.title,
            description: (data as typeof videoCollections[0]).description,
            thumbnailUrl: data.thumbnail,
            uploadDate: data.date || '2026-07-01',
          },
    ],
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords={`青青草视频,${data.title},${isArticle ? (data as typeof articles[0]).tags.join(',') : data.platform}`}
        canonicalUrl={`${siteConfig.url}/article/${data.id}`}
        ogType="article"
        schema={articleSchema}
      />
      <Navbar />
      <main className="pt-16">
        <article className="px-4 md:px-6 py-8">
          <div className="max-w-4xl mx-auto">
            <Breadcrumb
              items={[
                { label: isArticle ? (data as typeof articles[0]).category : '视频合集', path: `/category/${isArticle ? (data as typeof articles[0]).categorySlug : 'yingshidaquan'}` },
                { label: data.title },
              ]}
            />

            <header className="mb-8">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {data.title}
              </h1>
              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                {isArticle && (
                  <>
                    <span className="flex items-center gap-1">
                      <i className="ri-user-line"></i> {(data as typeof articles[0]).author}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-calendar-line"></i> {(data as typeof articles[0]).date}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {(data as typeof articles[0]).views.toLocaleString()} 次阅读
                    </span>
                  </>
                )}
                {!isArticle && (
                  <>
                    <span className="px-2 py-0.5 rounded-full bg-accent-100 text-accent-700 text-xs font-medium">
                      {data.platform}
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-eye-line"></i> {(data.views / 10000).toFixed(1)}万 次播放
                    </span>
                    <span className="flex items-center gap-1">
                      <i className="ri-star-fill text-accent-500"></i> {data.rating}
                    </span>
                  </>
                )}
              </div>
            </header>

            <div className="aspect-video rounded-xl overflow-hidden mb-10">
              <img
                src={data.thumbnail}
                alt={data.title}
                title={data.title}
                className="w-full h-full object-cover object-top"
              />
            </div>

            {isArticle && (
              <>
                <div className="flex flex-wrap gap-2 mb-8">
                  {(data as typeof articles[0]).tags.map((tag) => (
                    <span key={tag} className="px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium">
                      #{tag}
                    </span>
                  ))}
                </div>

                <div
                  className="prose prose-sm md:prose-base max-w-none text-foreground-800 leading-relaxed"
                  dangerouslySetInnerHTML={{
                    __html: (data as typeof articles[0]).content
                      .replace(/## (.*)/g, '<h2 class="text-xl md:text-2xl font-heading font-bold text-foreground-900 mt-8 mb-4">$1</h2>')
                      .replace(/\n\n/g, '</p><p class="mb-4">')
                      .replace(/^/, '<p class="mb-4">')
                      .replace(/$/, '</p>'),
                  }}
                />
              </>
            )}

            {!isArticle && (
              <div className="bg-background-100 rounded-xl p-6 text-center">
                <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-primary-100 mb-4">
                  <i className="ri-play-circle-fill text-primary-500 text-3xl"></i>
                </div>
                <p className="text-foreground-600 mb-4">点击下方按钮跳转至对应平台观看完整内容</p>
                <a
                  href="#"
                  className="inline-flex items-center gap-2 px-6 py-2.5 bg-primary-500 text-background-50 rounded-lg font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
                  onClick={(e) => e.preventDefault()}
                >
                  <i className="ri-external-link-line"></i> 在 {data.platform} 中观看
                </a>
              </div>
            )}

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-sm text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <i className="ri-arrow-left-line"></i> 返回首页
              </Link>
            </div>
          </div>
        </article>
      </main>
      <Footer />
    </>
  );
}